import React, { useCallback, useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import debounce from "lodash.debounce";
import { produce } from "immer";
import {
  Box,
  Typography,
  Card,
  IconButton,
  Collapse,
  TextField,
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
  Tooltip,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import QuizIcon from "@mui/icons-material/Quiz";
import SUGGESTED from "../../../assets/icons/suggested.webp";
import { PlanDropDown } from "../../../common";
import { Close } from "@mui/icons-material";

const QuestionCardEditable = ({
  question,
  testIndex,
  questionIndex,
  onDelete,
}) => {
  const { control, trigger, setValue, getValues, watch } = useFormContext();
  const [expanded, setExpanded] = useState(true);

  let currentQuestion = watch(`tests.${testIndex}.questions.${questionIndex}`);

  const mcqsOptions = useWatch({
    name: `tests.${testIndex}.questions.${questionIndex}.mcqs_options`,
    defaultValue: question?.mcqs_options || [],
  });

  let questionCategory =
    currentQuestion?.question_category || question?.question_category;

  const handleExpandClick = useCallback(() => {
    setExpanded((prevExpanded) => !prevExpanded);
  }, []);

  const updateTestAndQuestion = useCallback(
    (fieldPath, value, markEdited = true) => {
      const currentTests = getValues("tests");

      const updatedTests = produce(currentTests, (draft) => {
        const [testIndex] = fieldPath.split(".")[0].match(/\d+/);
        const questionIndex = fieldPath.split(".")[1].match(/\d+/);
        const fieldName = fieldPath.split(".").pop();

        if (fieldPath.includes("questions")) {
          draft[testIndex].questions[questionIndex][fieldName] = value;
          if (markEdited) {
            draft[testIndex].questions[questionIndex].isEdited = true;
            draft[testIndex].isEdited = true;
          }
        }
      });

      setValue("tests", updatedTests, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
      trigger(`tests[${testIndex}]`);
    },
    [getValues, setValue, trigger]
  );

  const debouncedUpdateTestAndQuestion = useCallback(
    debounce(updateTestAndQuestion, 1000),
    [updateTestAndQuestion]
  );

  const handleQuestionTextChange = useCallback(
    (e, field) => {
      const value = e?.target?.value;
      field.onChange(value);
      debouncedUpdateTestAndQuestion(
        `tests[${testIndex}].questions[${questionIndex}].question_text`,
        value
      );
    },
    [testIndex, questionIndex, debouncedUpdateTestAndQuestion]
  );

  const handleCorrectAnswerUpdate = useCallback(
    (e, field) => {
      field.onChange(e.target.value);
      debouncedUpdateTestAndQuestion(
        `tests[${testIndex}].questions[${questionIndex}].answer_text`,
        e.target.value
      );
    },
    [testIndex, questionIndex, debouncedUpdateTestAndQuestion]
  );

  const handleAddMCQOption = useCallback(() => {
    const currentTests = getValues("tests");
    const currentOptions =
      currentTests[testIndex].questions[questionIndex].mcqs_options || [];
    const updatedOptions = [...currentOptions, ""];

    setValue(
      `tests[${testIndex}].questions[${questionIndex}].mcqs_options`,
      updatedOptions,
      {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      }
    );

    updateTestAndQuestion(
      `tests[${testIndex}].questions[${questionIndex}].mcqs_options`,
      updatedOptions
    );
  }, [testIndex, questionIndex, getValues, setValue, updateTestAndQuestion]);

  const handleDeleteMCQOption = useCallback(
    (optionIndex) => {
      const currentTests = getValues("tests");
      const currentOptions = [
        ...(currentTests[testIndex].questions[questionIndex].mcqs_options ||
          []),
      ];
      const deletedOption = currentOptions[optionIndex];
      const updatedOptions = currentOptions.filter((_, i) => i !== optionIndex);

      console.log("Updated options:", updatedOptions);

      // Update options in form
      setValue(
        `tests[${testIndex}].questions[${questionIndex}].mcqs_options`,
        updatedOptions,
        {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true,
        }
      );

      // Clear the answer if the deleted option was selected
      if (
        currentTests[testIndex].questions[questionIndex].answer_text ===
        deletedOption
      ) {
        setValue(
          `tests[${testIndex}].questions[${questionIndex}].answer_text`,
          "",
          {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          }
        );
      }

      // Mark as edited
      const updatedTests = produce(currentTests, (draft) => {
        draft[testIndex].questions[questionIndex].isEdited = true;
        draft[testIndex].isEdited = true;
      });

      setValue("tests", updatedTests, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });

      trigger(`tests[${testIndex}]`);
    },
    [testIndex, questionIndex, getValues, setValue, trigger]
  );

  // Rest of the component remains the same...
  return (
    <Card
      sx={{
        mb: 2,
        p: 2,
        borderRadius: 2,
        bgcolor: "#FBFBFB",
      }}
    >
      {/* Question Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        {question?.suggested === "suggested" ? (
          <Box component="img" src={SUGGESTED} width={100} height={25} />
        ) : question?.id ? (
          <Box
            sx={{
              display: "inline-block",
              bgcolor: "#4BC7E1",
              px: 1.5,
              py: 0.5,
              borderRadius: 5,
              my: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: "bold",
                color: "white",
              }}
            >
              Question
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: "inline-block",
              py: 0.5,
              borderRadius: 5,
              mb: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: "14px",
                color: "black",
                fontWeight: 500,
              }}
            >
              Custom Question
            </Typography>
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            color: "text.secondary",
          }}
        >
          <QuizIcon fontSize="small" sx={{ mr: 0.5 }} />
          <Typography variant="body2" sx={{ mr: 2 }}>
            {question?.question_category === "multiple choice"
              ? "multiple choice"
              : "text"}
          </Typography>

          <Controller
            name={`tests[${testIndex}].questions[${questionIndex}].time_to_answer`}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <>
                <AccessTimeIcon
                  fontSize="small"
                  sx={{
                    mr: 0.5,
                    color: error?.message ? "#d32f2f" : "#646464",
                  }}
                />
                <PlanDropDown
                  data={[
                    "1 minute",
                    "2 minutes",
                    "3 minutes",
                    "4 minutes",
                    "5 minutes",
                    "6 minutes",
                    "7 minutes",
                    "8 minutes",
                  ]}
                  value={field.value}
                  triggerStyles={{
                    color: error?.message ? "#d32f2f" : "#646464",
                  }}
                  onChange={(value) => {
                    field.onChange(value);
                    setValue(`tests[${testIndex}].isEdited`, true);
                    setValue(
                      `tests[${testIndex}].questions[${questionIndex}.isEdited]`,
                      true
                    );
                    setValue(
                      `tests[${testIndex}].questions[${questionIndex}.isTimeEdited]`,
                      true
                    );
                  }}
                />
              </>
            )}
          />

          <IconButton
            onClick={handleExpandClick}
            sx={{
              marginLeft: "auto",
              transition: "transform 0.3s",
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            }}
          >
            <ExpandMoreIcon />
          </IconButton>
          <IconButton
            onClick={() => onDelete(questionIndex)}
            sx={{
              borderColor: "red",
              border: "1px solid red",
              height: 15,
              width: 15,
              p: 1.5,
            }}
            size="small"
          >
            <Close sx={{ color: "red", height: 15, width: 15 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Question Text */}
      <Controller
        name={`tests[${testIndex}].questions[${questionIndex}].question_text`}
        control={control}
        rules={{ shouldUnregister: true }}
        defaultValue={question?.question_text || ""}
        render={({ field, fieldState: { error } }) => (
          <TextField
            fullWidth
            multiline
            rows={2}
            variant="outlined"
            sx={{ mb: 2 }}
            placeholder="Type your question here"
            error={!!error}
            helperText={error && error?.message}
            onChange={(e) => handleQuestionTextChange(e, field)}
            value={field.value}
          />
        )}
      />

      <Box
        sx={{
          display: "inline-block",
          bgcolor: "#4BC7E1",
          px: 1.5,
          py: 0.5,
          borderRadius: 5,
          mt: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: "12px",
            fontWeight: "bold",
            color: "white",
          }}
        >
          Expected Answer
        </Typography>
      </Box>

      {/* Options */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box sx={{ mt: 2, bgcolor: "#f9f9f9", borderRadius: 1 }}>
          <Controller
            name={`tests[${testIndex}].questions[${questionIndex}].answer_text`}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <>
                {questionCategory === "multiple choice" ? (
                  <>
                    <RadioGroup
                      value={field?.value || null}
                      onChange={(e) => handleCorrectAnswerUpdate(e, field)}
                    >
                      {mcqsOptions?.map((option, index) => {
                        const letter = String.fromCharCode(65 + index);
                        return (
                          <Box
                            key={`option-${testIndex}-${questionIndex}-${index}-${option}`} // Include option in key
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 2,
                              gap: 1,
                              position: "relative",
                            }}
                          >
                            <FormControlLabel
                              value={option}
                              control={
                                <Radio
                                  sx={{
                                    color: "#4BC7E1",
                                    "&.Mui-checked": {
                                      color: "#4BC7E1",
                                    },
                                  }}
                                />
                              }
                              label={`${letter}:`}
                              sx={{ minWidth: 45 }}
                            />
                            <Controller
                              name={`tests[${testIndex}].questions[${questionIndex}].mcqs_options[${index}]`}
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  fullWidth
                                  placeholder={
                                    index <= 1
                                      ? "Type correct answer here"
                                      : "Type other options here"
                                  }
                                  variant="outlined"
                                  size="small"
                                  sx={{ flex: 1 }}
                                  {...field}
                                />
                              )}
                            />
                            <Tooltip title="Remove option">
                              <IconButton
                                onClick={() => handleDeleteMCQOption(index)}
                                size="small"
                                sx={{
                                  color: "#f44336",
                                  visibility:
                                    (mcqsOptions || []).length <= 2
                                      ? "hidden"
                                      : "visible",
                                }}
                              >
                                <DeleteOutlineIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        );
                      })}
                    </RadioGroup>
                    {mcqsOptions?.length < 4 && (
                      <Button
                        startIcon={<AddCircleOutlineIcon />}
                        onClick={handleAddMCQOption}
                        sx={{ mt: 1, color: "text.secondary" }}
                      >
                        Add option
                      </Button>
                    )}
                    {/* Error Message for less than 2 options */}
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        color: error?.message ? "#d32f2f" : "text.secondary",
                        mt: 1,
                      }}
                    >
                      {mcqsOptions?.length < 2
                        ? "Please add at least 2 options"
                        : error?.message}
                    </Typography>

                    {/* Error Message for no correct answer selected */}
                    {mcqsOptions?.length >= 2 &&
                      !currentQuestion?.answer_text && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            color: "#d32f2f", // Error color
                            mt: 1,
                          }}
                        >
                          Please choose at least 1 correct answer
                        </Typography>
                      )}
                  </>
                ) : (
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    variant="outlined"
                    placeholder="Type expected answer here"
                    onChange={(e) => handleCorrectAnswerUpdate(e, field)}
                    error={!!error}
                    helperText={error && error?.message}
                    value={field.value}
                  />
                )}
              </>
            )}
          />
        </Box>
      </Collapse>
    </Card>
  );
};

export default QuestionCardEditable;
